import React, { useState, KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { Button } from '@/components/UI/Button';
import { IconFont } from '@/components/IconFont';
import './SendTestEmailModal.scss';

// Email validation regex from bay-ui
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const MAX_EMAILS = 5;

export interface SendTestEmailModalProps {
  isVisible: boolean;
  onClose: () => void;
  htmlContent: string;
  subject: string;
  onSendTestEmail?: (emails: string[], html: string, subject: string) => Promise<void>;
  userEmail?: string;
  userName?: string;
}

export function SendTestEmailModal({
  isVisible,
  onClose,
  htmlContent,
  subject,
  onSendTestEmail,
  userEmail,
  userName
}: SendTestEmailModalProps) {
  const [emailChips, setEmailChips] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string>('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const modalBackdropClassName = clsx([
    'send-test-modal-backdrop position-fixed top-0 start-0 end-0 bottom-0',
    isVisible ? 'opacity-100 pe-auto' : 'opacity-0 pe-none',
  ]);
  const modalClassName = clsx([
    'send-test-modal position-fixed top-50 start-50',
    isVisible ? 'send-test-modal--visible opacity-100 pe-auto' : 'opacity-0 pe-none',
  ]);

  const handleAddMyEmail = () => {
    // Use logged-in user's email - should be passed from parent
    const currentUserEmail = userEmail || 'user@example.com';

    if (!emailChips.includes(currentUserEmail) && emailChips.length < MAX_EMAILS) {
      setEmailChips([...emailChips, currentUserEmail]);
      setError('');
    } else if (emailChips.length >= MAX_EMAILS) {
      setError(`You've reached the limit of ${MAX_EMAILS} email addresses.`);
    }
  };

  const handleRemoveChip = (emailToRemove: string) => {
    setEmailChips(emailChips.filter(email => email !== emailToRemove));
    setError('');
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addEmailChip();
    } else if (e.key === 'Backspace' && inputValue === '' && emailChips.length > 0) {
      // Remove last chip when backspace is pressed on empty input
      setEmailChips(emailChips.slice(0, -1));
      setError('');
    }
  };

  const addEmailChip = () => {
    const email = inputValue.trim().replace(/[,\s]+$/g, ''); // Remove trailing comma/spaces

    if (!email) {
      return;
    }

    // Check if limit reached
    if (emailChips.length >= MAX_EMAILS) {
      setError(`You've reached the limit of ${MAX_EMAILS} email addresses.`);
      return;
    }

    // Validate email format
    if (!EMAIL_REGEX.test(email)) {
      setError('The email address you entered is invalid. Please check the format and try again.');
      return;
    }

    // Check for duplicates
    if (emailChips.includes(email)) {
      setError('This email address has already been added.');
      return;
    }

    // Add email and clear error
    setEmailChips([...emailChips, email]);
    setInputValue('');
    setError('');
  };

  const handleInputBlur = () => {
    setIsInputFocused(false);
    if (inputValue.trim()) {
      addEmailChip();
    }
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
  };

  const handleClose = () => {
    if(isSending) return; // Prevent closing while sending
    setEmailChips([]);
    setInputValue('');
    setError('');
    setIsInputFocused(false);
    onClose();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleSendTest = async () => {
    if (emailChips.length === 0) {
      setError('Please add at least one email address');
      return;
    }

    if (isSending) {
      return; // Prevent double submission
    }

    try {
      setIsSending(true);
      setError('');

      if (onSendTestEmail) {
        await onSendTestEmail(emailChips, htmlContent, `Test - ${subject}`);
      }

      // Success - close modal and reset
      setEmailChips([]);
      setInputValue('');
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to send test email. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={modalBackdropClassName}
        onClick={handleClose}
      />

      {/* Modal */}
      <div className={modalClassName}>
        <div className='send-test-modal__container bg-white d-flex flex-column rounded'>
          {/* Body */}
          <div className='send-test-modal__body p-5 d-flex flex-column gap-4'>
            <div className='d-flex flex-column gap-3'>
              <div className='d-flex justify-content-between align-items-center'>
                <h3 className='send-test-modal__title m-0 fs-4 fw-bold text-dark'>Send Test Email</h3>
                <div className='d-flex align-items-center'>
                  <button
                    type='button'
                    onClick={handleClose}
                    className='send-test-modal__close-icon text-700 border-0 p-1 cursor-pointer d-flex align-items-center justify-content-center rounded-1'
                  >
                    <IconFont iconName='icon-close' />
                  </button>
                </div>
              </div>

              <p className='send-test-modal__description fs-6 text-900 m-0 lh-base'>
                You can enter up to 5 email addresses. A "Test -" prefix will be added to the subject line of all test emails to help you identify them.
              </p>
            </div>

            <div className='send-test-modal__form d-flex flex-column'>
              <label className='send-test-modal__label mb-1 fs-6 fw-bold text-black'>Email Addresses</label>
              <div className='send-test-modal__input-group d-flex align-items-center gap-3'>
                <div
                  className={clsx('send-test-modal__input-wrapper bg-white position-relative d-flex align-items-center flex-1', {
                    'send-test-modal__input-wrapper--error': error,
                    'send-test-modal__input-wrapper--focused': isInputFocused,
                  })}
                >
                  <div className='send-test-modal__chips-container d-flex flex-wrap align-items-center flex-1 gap-2 p-3'>
                    {emailChips.map((email, index) => (
                      <div key={index} className='send-test-modal__chip bg-100 fs-6 d-inline-flex align-items-center gap-1 py-1 pe-2 ps-3 rounded-1'>
                        <span className='send-test-modal__chip-text text-900'>{email}</span>
                        <button
                          type='button'
                          className='send-test-modal__chip-remove text-700 border-0 cursor-pointer d-flex align-items-center justify-content-center flex-shrink-0'
                          onClick={() => handleRemoveChip(email)}
                        >
                          <IconFont iconName='icon-close fs-8' />
                        </button>
                      </div>
                    ))}
                    <input
                      type='text'
                      className='send-test-modal__input fs-6 p-1 border-0'
                      placeholder={emailChips.length === 0 ? 'Enter email addresses' : ''}
                      value={inputValue}
                      onChange={handleInputChange}
                      onKeyDown={handleInputKeyDown}
                      onBlur={handleInputBlur}
                      onFocus={handleInputFocus}
                    />
                  </div>
                </div>
                <button
                  type='button'
                  className='send-test-modal__add-email-btn fs-6 border-0 px-0 py-2 fw-medium cursor-pointer text-nowrap bg-white text-blue-500'
                  onClick={handleAddMyEmail}
                  disabled={emailChips.length >= MAX_EMAILS}
                >
                  + Add My Email
                </button>
              </div>
              {error && (
                <div className='send-test-modal__error-message fs-6 mt-2 fw-normal text-red lh-base'>
                  {error}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className='send-test-modal__footer d-flex justify-content-between align-items-center px-5 py-4 border-top border-200'>
            <button
              type='button'
              className='send-test-modal__cancel-btn btn fw-medium cursor-pointer fs-6 text-600 bg-white border border-secondary rounded-1'
              onClick={handleClose}
              disabled={isSending}
            >
              Cancel
            </button>
            <button
              type='button'
              className='send-test-modal__send-btn btn fw-medium cursor-pointer fs-6 border-0 text-white bg-primary rounded-1'
              onClick={handleSendTest}
              disabled={emailChips.length === 0 || isSending || !!error}
            >
              {isSending ? 'Sending...' : 'Send Test Email'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
